import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addBreadcrumb } from '@sentry/react';

import { PlacesAndAutocomplete } from '@ocx-app/lib/geolocation/PlacesAndAutocomplete';
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';

import { useLocalOffersLocationContext } from '../lib/context/use-local-offers-location-context.hook';
import { ListItem } from './local-offers-location-selector.types';

type UseLocalOffersStoreSelectorParams = {
  onCoordinatesNotFound: () => void;
};

export const useLocalOffersLocationSelector = ({ onCoordinatesNotFound }: UseLocalOffersStoreSelectorParams) => {
  const googleMapsApiKey = useConfiguration('storeLocator.googleMapsApiKey');
  const [stores, setStores] = useState<ListItem[]>([]);
  const { setCustomLocation, requestCurrentLocation } = useLocalOffersLocationContext();
  const navigate = useNavigate();
  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleLocationSelect = useCallback(
    async (item: ListItem) => {
      const coordinates = await PlacesAndAutocomplete.getPlaceCoordinates(item.uuid);
      if (!coordinates) {
        onCoordinatesNotFound();
        return;
      }
      setCustomLocation({ name: item.title, coordinates, placeId: item.uuid });
      navigateBack();
    },
    [navigateBack, onCoordinatesNotFound, setCustomLocation],
  );

  const marketingTitle = useConfiguration('marketing.title');
  const [noResults, setNoResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleCurrentLocationClick = useCallback(() => {
    requestCurrentLocation();
    navigateBack();
  }, [navigateBack, requestCurrentLocation]);

  const onSearchSubmit = useCallback(
    async (search: string) => {
      if (loading) {
        return;
      }

      setNoResults(false);
      if (!search) {
        setStores([]);
        return;
      }

      if (!PlacesAndAutocomplete.isPlacesAvailable()) {
        addBreadcrumb({ message: '[Google PlacesAndAutocomplete] Places is not available' });
        return;
      }

      setLoading(true);

      const places = await PlacesAndAutocomplete.getPlacesByStringSearch(search);

      setLoading(false);

      const sanitizedStores = places.map<ListItem>(({ id, mainText, secondaryText }) => ({
        title: mainText,
        subtitle: secondaryText,
        uuid: id,
      }));

      setStores(sanitizedStores);

      if (!sanitizedStores.length) {
        setNoResults(true);
      }
    },
    [loading],
  );

  return useMemo(
    () => ({
      loading,
      googleMapsApiKey,
      stores,
      handleLocationSelect,
      marketingTitle,
      noResults,
      handleCurrentLocationClick,
      onSearchSubmit,
    }),
    [
      loading,
      googleMapsApiKey,
      handleCurrentLocationClick,
      handleLocationSelect,
      noResults,
      marketingTitle,
      onSearchSubmit,
      stores,
    ],
  );
};
